import {
    ref,
    uploadBytesResumable,
    getDownloadURL,
    deleteObject,
    UploadTask,
    UploadTaskSnapshot,
} from 'firebase/storage';
import { storage } from './firebaseConfig';
import { getCurrentUser } from './authService';

export interface UploadProgress {
    progress: number; // 0-100
    bytesTransferred: number;
    totalBytes: number;
}

export interface MediaMetadata {
    contentType: string;
    size: number;
    uploadedAt: string;
    storagePath: string;
    downloadURL: string;
}

/**
 * Get current user ID for storage paths
 */
const getCurrentUserId = (): string => {
    const user = getCurrentUser();
    if (!user) {
        throw new Error('User not authenticated');
    }
    return user.uid;
};

/**
 * Check if the browser supports WebP encoding via canvas.
 * Cached after first call.
 */
let _webpSupported: boolean | null = null;
function supportsWebP(): boolean {
    if (_webpSupported !== null) return _webpSupported;
    try {
        const c = document.createElement('canvas');
        c.width = 1; c.height = 1;
        _webpSupported = c.toDataURL('image/webp').startsWith('data:image/webp');
    } catch {
        _webpSupported = false;
    }
    return _webpSupported;
}

/**
 * Get tiered compression settings based on file size.
 * Larger files get more aggressive compression.
 */
function getCompressionTier(fileSize: number): { maxWidth: number; quality: number } {
    if (fileSize > 15 * 1024 * 1024) return { maxWidth: 1280, quality: 0.65 };
    if (fileSize > 5 * 1024 * 1024)  return { maxWidth: 1600, quality: 0.7 };
    return { maxWidth: 1920, quality: 0.8 };
}

/**
 * Compress image before upload using canvas.
 * Uses tiered compression based on file size and outputs WebP where supported
 * (30-50% smaller than JPEG at equivalent quality). Canvas drawing naturally
 * strips EXIF metadata, keeping only the pixel data.
 */
const compressImage = async (file: File, maxWidth?: number, quality?: number): Promise<Blob> => {
    // Use explicit params if provided (e.g. profile photo), otherwise tier by file size
    const tier = (maxWidth !== undefined && quality !== undefined)
        ? { maxWidth, quality }
        : getCompressionTier(file.size);

    const outputType = supportsWebP() ? 'image/webp' : 'image/jpeg';

    return new Promise((resolve, reject) => {
        const img = new Image();
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        img.onload = () => {
            URL.revokeObjectURL(img.src);
            let width = img.width;
            let height = img.height;

            // Calculate new dimensions maintaining aspect ratio
            if (width > tier.maxWidth) {
                height = (height * tier.maxWidth) / width;
                width = tier.maxWidth;
            }

            canvas.width = width;
            canvas.height = height;

            ctx?.drawImage(img, 0, 0, width, height);

            canvas.toBlob(
                (blob) => {
                    if (blob) {
                        resolve(blob);
                    } else {
                        reject(new Error('Failed to compress image'));
                    }
                },
                outputType,
                tier.quality,
            );
        };

        img.onerror = () => {
            URL.revokeObjectURL(img.src);
            reject(new Error('Failed to load image'));
        };
        img.src = URL.createObjectURL(file);
    });
};

/**
 * Upload photo to Firebase Storage
 */
export const uploadPhoto = async (
    file: File,
    submissionId: string,
    onProgress?: (progress: UploadProgress) => void
): Promise<MediaMetadata> => {
    try {
        const userId = getCurrentUserId();

        // Compress image before upload
        let uploadFile: File | Blob = file;
        if (file.size > 1024 * 1024) { // Only compress if larger than 1MB
            uploadFile = await compressImage(file);
        }

        const fileName = `${Date.now()}_${file.name}`;
        const storagePath = `users/${userId}/submissions/${submissionId}/${fileName}`;
        const storageRef = ref(storage, storagePath);

        const uploadTask = uploadBytesResumable(storageRef, uploadFile, {
            contentType: file.type,
        });

        return new Promise((resolve, reject) => {
            uploadTask.on(
                'state_changed',
                (snapshot) => {
                    const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    onProgress?.({
                        progress,
                        bytesTransferred: snapshot.bytesTransferred,
                        totalBytes: snapshot.totalBytes,
                    });
                },
                (error) => {
                    console.error('Upload error:', error);
                    reject(new Error('Failed to upload photo'));
                },
                async () => {
                    const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                    resolve({
                        contentType: file.type,
                        size: uploadTask.snapshot.totalBytes,
                        uploadedAt: new Date().toISOString(),
                        storagePath,
                        downloadURL,
                    });
                }
            );
        });
    } catch (error) {
        console.error('Error uploading photo:', error);
        throw error;
    }
};

/**
 * Upload video to Firebase Storage
 */
export const uploadVideo = async (
    file: File,
    submissionId: string,
    onProgress?: (progress: UploadProgress) => void
): Promise<MediaMetadata> => {
    try {
        const userId = getCurrentUserId();

        const fileName = `${Date.now()}_${file.name}`;
        const storagePath = `users/${userId}/submissions/${submissionId}/${fileName}`;
        const storageRef = ref(storage, storagePath);

        const uploadTask = uploadBytesResumable(storageRef, file, {
            contentType: file.type,
        });

        return new Promise((resolve, reject) => {
            uploadTask.on(
                'state_changed',
                (snapshot) => {
                    const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    onProgress?.({
                        progress,
                        bytesTransferred: snapshot.bytesTransferred,
                        totalBytes: snapshot.totalBytes,
                    });
                },
                (error) => {
                    console.error('Upload error:', error);
                    const code = (error as any)?.code || 'unknown';
                    const msg = (error as any)?.message || String(error);
                    reject(new Error(`Video upload failed (${code}): ${msg}`));
                },
                async () => {
                    const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                    resolve({
                        contentType: file.type,
                        size: uploadTask.snapshot.totalBytes,
                        uploadedAt: new Date().toISOString(),
                        storagePath,
                        downloadURL,
                    });
                }
            );
        });
    } catch (error) {
        console.error('Error uploading video:', error);
        throw error;
    }
};

/**
 * Upload audio to Firebase Storage
 */
export const uploadAudio = async (
    file: File,
    submissionId: string,
    onProgress?: (progress: UploadProgress) => void
): Promise<MediaMetadata> => {
    try {
        const userId = getCurrentUserId();

        const fileName = `${Date.now()}_${file.name}`;
        const storagePath = `users/${userId}/submissions/${submissionId}/${fileName}`;
        const storageRef = ref(storage, storagePath);

        const uploadTask = uploadBytesResumable(storageRef, file, {
            contentType: file.type,
        });

        return new Promise((resolve, reject) => {
            uploadTask.on(
                'state_changed',
                (snapshot) => {
                    const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    onProgress?.({
                        progress,
                        bytesTransferred: snapshot.bytesTransferred,
                        totalBytes: snapshot.totalBytes,
                    });
                },
                (error) => {
                    console.error('Upload error:', error);
                    reject(new Error('Failed to upload audio'));
                },
                async () => {
                    const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                    resolve({
                        contentType: file.type,
                        size: uploadTask.snapshot.totalBytes,
                        uploadedAt: new Date().toISOString(),
                        storagePath,
                        downloadURL,
                    });
                }
            );
        });
    } catch (error) {
        console.error('Error uploading audio:', error);
        throw error;
    }
};

/**
 * Upload profile photo
 */
export const uploadProfilePhoto = async (
    file: File,
    onProgress?: (progress: UploadProgress) => void
): Promise<MediaMetadata> => {
    try {
        const userId = getCurrentUserId();

        // Compress profile photo
        const compressedImage = await compressImage(file, 512, 0.85);

        const fileName = `avatar_${Date.now()}.jpg`;
        const storagePath = `users/${userId}/profile/${fileName}`;
        const storageRef = ref(storage, storagePath);

        const uploadTask = uploadBytesResumable(storageRef, compressedImage, {
            contentType: 'image/jpeg',
        });

        return new Promise((resolve, reject) => {
            uploadTask.on(
                'state_changed',
                (snapshot) => {
                    const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    onProgress?.({
                        progress,
                        bytesTransferred: snapshot.bytesTransferred,
                        totalBytes: snapshot.totalBytes,
                    });
                },
                (error) => {
                    console.error('Upload error:', error);
                    reject(new Error('Failed to upload profile photo'));
                },
                async () => {
                    const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                    resolve({
                        contentType: 'image/jpeg',
                        size: uploadTask.snapshot.totalBytes,
                        uploadedAt: new Date().toISOString(),
                        storagePath,
                        downloadURL,
                    });
                }
            );
        });
    } catch (error) {
        console.error('Error uploading profile photo:', error);
        throw error;
    }
};

/**
 * Delete media from Firebase Storage
 */
export const deleteMedia = async (storagePath: string): Promise<void> => {
    try {
        const storageRef = ref(storage, storagePath);
        await deleteObject(storageRef);
    } catch (error) {
        console.error('Error deleting media:', error);
        throw error;
    }
};

/**
 * Get download URL for a storage path
 */
export const getMediaDownloadURL = async (storagePath: string): Promise<string> => {
    try {
        const storageRef = ref(storage, storagePath);
        return await getDownloadURL(storageRef);
    } catch (error) {
        console.error('Error getting download URL:', error);
        throw error;
    }
};

/**
 * Upload media based on type
 */
/**
 * Upload document to Firebase Storage
 */
export const uploadDocument = async (
    file: File,
    submissionId: string,
    onProgress?: (progress: UploadProgress) => void
): Promise<MediaMetadata> => {
    try {
        const userId = getCurrentUserId();

        const fileName = `${Date.now()}_${file.name}`;
        const storagePath = `users/${userId}/submissions/${submissionId}/${fileName}`;
        const storageRef = ref(storage, storagePath);

        const uploadTask = uploadBytesResumable(storageRef, file, {
            contentType: file.type,
        });

        return new Promise((resolve, reject) => {
            uploadTask.on(
                'state_changed',
                (snapshot) => {
                    const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    onProgress?.({
                        progress,
                        bytesTransferred: snapshot.bytesTransferred,
                        totalBytes: snapshot.totalBytes,
                    });
                },
                (error) => {
                    console.error('Upload error:', error);
                    reject(new Error('Failed to upload document'));
                },
                async () => {
                    const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                    resolve({
                        contentType: file.type,
                        size: uploadTask.snapshot.totalBytes,
                        uploadedAt: new Date().toISOString(),
                        storagePath,
                        downloadURL,
                    });
                }
            );
        });
    } catch (error) {
        console.error('Error uploading document:', error);
        throw error;
    }
};

/**
 * Upload media based on type
 */
export const uploadMedia = async (
    file: File,
    mediaType: 'photo' | 'video' | 'audio' | 'document' | 'screen',
    submissionId: string,
    onProgress?: (progress: UploadProgress) => void
): Promise<MediaMetadata> => {
    switch (mediaType) {
        case 'photo':
            return uploadPhoto(file, submissionId, onProgress);
        case 'video':
        case 'screen':
            return uploadVideo(file, submissionId, onProgress);
        case 'audio':
            return uploadAudio(file, submissionId, onProgress);
        case 'document':
            return uploadDocument(file, submissionId, onProgress);
        default:
            throw new Error('Unsupported media type: ' + mediaType);
    }
};

/**
 * Upload draft media and return its object path as well as its URL.
 *
 * Used only when IndexedDB refuses to store Blobs (iOS in-app browsers) — see
 * draftStorageService. The path is what makes deletion on submit/draft-delete
 * possible; uploadBlobToStorage returns a URL alone and cannot be cleaned up.
 *
 * Deliberately under users/{uid}/submissions/... so it inherits the existing
 * owner-only rule. No submission document references it, so researchers never
 * see it; it is deleted when the draft is submitted or discarded.
 */
export const uploadDraftAsset = async (
    blob: Blob,
    taskId: string,
    index: number,
): Promise<{ url: string; path: string }> => {
    const userId = getCurrentUserId();
    const ext = blob.type.includes('video') ? 'webm'
        : blob.type.includes('audio') ? 'webm'
            : blob.type.includes('image') ? 'jpg'
                : 'bin';
    // randomUUID, not Date.now(): a clock correction can move Date.now()
    // backward, and colliding paths would overwrite another save's media.
    const path = `users/${userId}/submissions/draft-${taskId}/${crypto.randomUUID()}_${index}.${ext}`;
    const storageRef = ref(storage, path);
    const task = uploadBytesResumable(storageRef, blob, {
        contentType: blob.type || 'application/octet-stream',
    });
    await task;
    const url = await getDownloadURL(task.snapshot.ref);
    return { url, path };
};

/**
 * Upload a raw Blob to Firebase Storage (used by offline sync pipeline).
 * Returns the permanent download URL.
 */
export const uploadBlobToStorage = async (
    blob: Blob,
    mediaType: string,
    submissionId: string
): Promise<string> => {
    const userId = getCurrentUserId();
    const ext = blob.type.includes('video') ? 'webm'
        : blob.type.includes('audio') ? 'webm'
            : blob.type.includes('image') ? 'jpg'
                : 'bin';
    const fileName = `${Date.now()}_offline.${ext}`;
    const storagePath = `users/${userId}/submissions/${submissionId}/${fileName}`;
    const storageRef = ref(storage, storagePath);

    const uploadTask = uploadBytesResumable(storageRef, blob, {
        contentType: blob.type || 'application/octet-stream',
    });

    return new Promise((resolve, reject) => {
        uploadTask.on(
            'state_changed',
            () => { }, // No progress tracking needed for background sync
            (error) => {
                console.error('Blob upload error:', error);
                reject(new Error('Failed to upload offline media'));
            },
            async () => {
                const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                resolve(downloadURL);
            }
        );
    });
};
